import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import {
  ChevronLeft,
  ChevronRight,
  Users,
  Wrench,
  HardHat,
  Store,
  Database,
  Calculator,
  Headphones,
  X,
  GraduationCap,
  Code,
  Cpu,
  UserCog,
  Briefcase,
  Grid,
} from 'lucide-react';

const iconMap = {
  'Sales / Marketing': Users,
  'Sales Executive': Users,
  Mechanic: Wrench,
  Engineer: HardHat,
  'Retail / Store Executive': Store,
  'Data Entry / Back Office': Database,
  Accountant: Calculator,
  'BPO / Customer care': Headphones,
  'Customer Service / Tech Support': Headphones,
  'Management Trainee': GraduationCap,
  'Tech Support': Code,
  HR: UserCog,
  'IT Hardware Engineer': Cpu,
};

const TopRoles = () => {
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showRolesModal, setShowRolesModal] = useState(false);
  const [showPopup, setShowPopup] = useState(true);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const fetchTopCategories = async () => {
      try {
        const response = await api.get('/top-categories');
        const fetchedData = response.data.data || response.data;
        setCategories(Array.isArray(fetchedData) ? fetchedData : []);
      } catch (error) {
        console.error('Error fetching top categories', error);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTopCategories();
  }, []);

  const closePopup = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowPopup(false);
    }, 500);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      closePopup();
    }, 4000);
    return () => clearTimeout(timer);
  }, []);

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: dir * 320,
        behavior: 'smooth',
      });
    }
  };

  const handleCategoryClick = (e, categoryName) => {
    e.preventDefault();
    setShowRolesModal(false);
    navigate(`/job-listing?category=${encodeURIComponent(categoryName)}`);
  };

  const safeCategories = Array.isArray(categories) ? categories : [];
  const sliderCategories = safeCategories.slice(0, 6);

  return (
    <section className="bg-white dark:bg-gray-900 -mt-12 relative z-10 transition-colors duration-300">
      <div className="max-w-[1200px] mx-auto px-4">
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700 p-6 md:p-8 lg:p-10 transition-colors duration-300">
          
          {/* Header Section */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">Top Job Categories</h2>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mt-1.5">Discover trending opportunities across different sectors</p>
            </div>
            
            {/* Scroll Buttons */}
            <div className="hidden sm:flex items-center gap-3">
              <button
                onClick={() => scroll(-1)}
                className="w-10 h-10 rounded-full border-2 border-gray-200 dark:border-gray-700 flex items-center justify-center bg-white dark:bg-gray-800 hover:bg-[#FED547] hover:border-[#FED547] hover:text-black dark:text-gray-300 transition-all duration-300 shadow-sm"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => scroll(1)}
                className="w-10 h-10 rounded-full border-2 border-gray-200 dark:border-gray-700 flex items-center justify-center bg-white dark:bg-gray-800 hover:bg-[#FED547] hover:border-[#FED547] hover:text-black dark:text-gray-300 transition-all duration-300 shadow-sm"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Slider Container */}
          <div
            ref={scrollRef}
            className="flex gap-5 overflow-x-auto scroll-smooth pb-4 -mx-2 px-2 items-stretch"
            style={{ scrollbarWidth: 'none' }}
          >
            <style>{`section .flex::-webkit-scrollbar{display:none}`}</style>

            {loading ? (
              <div className="flex gap-5">
                {[1, 2, 3, 4].map((n) => (
                  <div key={n} className="shrink-0 w-[200px] h-[160px] bg-gray-100 dark:bg-gray-800 rounded-2xl animate-pulse"></div>
                ))}
              </div>
            ) : safeCategories.length === 0 ? (
              <div className="text-sm font-semibold text-gray-500 py-4 px-2">No active job categories found.</div>
            ) : (
              sliderCategories.map((cat, i) => {
                const Icon = iconMap[cat.category] || Briefcase;
                return (
                  <button
                    key={i}
                    onClick={(e) => handleCategoryClick(e, cat.category)}
                    className="group relative text-left shrink-0 w-[210px] rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 p-5 hover:border-[#FED547] hover:shadow-xl hover:shadow-[#FED547]/10 hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between"
                  >
                    {i < 3 && (
                      <span className="absolute top-4 right-4 text-[10px] font-extrabold uppercase text-gray-900 bg-[#FED547] px-2 py-0.5 rounded shadow-sm">
                        Hot
                      </span>
                    )}
                    <div className="w-12 h-12 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-100 dark:border-gray-600 flex items-center justify-center mb-4 group-hover:bg-[#FED547] group-hover:border-[#FED547] group-hover:scale-110 transition-all duration-300 shadow-sm">
                      <Icon className="w-6 h-6 text-gray-400 dark:text-gray-300 group-hover:text-black transition-colors" />
                    </div>
                    <div>
                      <div className="font-bold text-[15px] text-gray-900 dark:text-white leading-tight line-clamp-2 mb-2 group-hover:text-blue-600 transition-colors">
                        {cat.category}
                      </div>
                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                        </span>
                        {cat.active_jobs} Active Jobs
                      </div>
                    </div>
                  </button>
                );
              })
            )}

            {/* View All Button inside Slider */}
            {safeCategories.length > 6 && (
              <button
                type="button"
                onClick={() => setShowRolesModal(true)}
                className="group shrink-0 ml-2 px-6 w-[160px] text-center flex flex-col items-center justify-center text-[14px] font-bold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 hover:bg-[#FED547] hover:text-black border-2 border-dashed border-gray-200 dark:border-gray-700 hover:border-transparent rounded-2xl transition-all shadow-sm cursor-pointer"
              >
                <Grid className="w-6 h-6 mb-2 text-gray-400 group-hover:text-black transition-colors" />
                View All<br />Categories
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 🌟 ALL CATEGORIES MODAL 🌟 */}
      {showRolesModal && createPortal(
        <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 sm:p-6 transition-opacity">
          <div className="bg-white dark:bg-gray-900 rounded-3xl w-full max-w-6xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col animate-in zoom-in-95 duration-200 border border-gray-200 dark:border-gray-800">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between px-8 py-6 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 border-b border-gray-200 dark:border-gray-700 shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-orange-50 dark:bg-gray-800 rounded-2xl border border-orange-100 dark:border-gray-700 flex items-center justify-center shadow-sm">
                  <Grid className="w-7 h-7 text-[#F58634]" />
                </div>
                <div>
                  <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight">Explore All Categories</h2>
                  <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 mt-1">
                    Showing <span className="text-[#F58634] font-bold">{safeCategories.length}</span> active categories
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowRolesModal(false)}
                className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-500 hover:rotate-90 flex items-center justify-center transition-all duration-300 text-gray-500 dark:text-gray-400 border border-transparent hover:border-red-100 dark:hover:border-red-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Grid Content */}
            <div className="p-8 overflow-y-auto bg-gray-50/50 dark:bg-gray-900/40 flex-1 custom-scrollbar">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                {safeCategories.map((cat, index) => {
                  const Icon = iconMap[cat.category] || Briefcase;
                  return (
                    <button
                      key={index}
                      onClick={(e) => handleCategoryClick(e, cat.category)}
                      className="group relative text-left bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 hover:border-[#FED547] hover:shadow-xl hover:shadow-[#FED547]/10 hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between"
                    >
                      {index < 3 && (
                        <span className="absolute top-4 right-4 text-[10px] font-extrabold tracking-wider uppercase text-gray-900 bg-[#FED547] px-2 py-0.5 rounded shadow-sm">
                          HOT
                        </span>
                      )}
                      <div className="w-12 h-12 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-100 dark:border-gray-600 flex items-center justify-center mb-4 group-hover:bg-[#FED547] group-hover:border-[#FED547] group-hover:scale-110 transition-all duration-300">
                        <Icon className="w-6 h-6 text-gray-400 dark:text-gray-300 group-hover:text-black transition-colors" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 dark:text-white text-[15px] leading-tight mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                          {cat.category}
                        </h3>
                        <p className="text-[12px] font-semibold text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                          </span>
                          {cat.active_jobs} Active Jobs
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-8 py-5 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex items-center justify-center shrink-0">
              <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                Can't find what you're looking for?{' '}
                <a href="/job-listing" className="text-blue-600 dark:text-blue-400 hover:underline">
                  View all active jobs
                </a>
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
};

export default TopRoles;